// AIDEV-NOTE: Dialog acionado quando checkin falha por falta de coordenadas GPS do cliente.
// Tenta geocodificar automaticamente; se falhar, abre form para o usuário corrigir o endereço.
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import api from '@/services/auth.service';
import type { AxiosError } from 'axios';

function extractApiError(err: unknown): string {
  const axiosErr = err as AxiosError<{ error?: string }>;
  return (
    axiosErr?.response?.data?.error ?? (err instanceof Error ? err.message : 'Erro desconhecido')
  );
}

type Phase = 'geocodificando' | 'editando' | 'buscando_manual' | 'nao_encontrado';

interface Props {
  open: boolean;
  clienteId: string;
  clienteNome: string;
  enderecoAtual?: string;
  onSuccess: () => void;
  onClose: () => void;
}

export function GeocodingCheckinDialog({
  open,
  clienteId,
  clienteNome,
  enderecoAtual,
  onSuccess,
  onClose
}: Props) {
  const [phase, setPhase] = useState<Phase>('geocodificando');
  const [enderecoManual, setEnderecoManual] = useState('');
  const [erroMsg, setErroMsg] = useState('');

  // Inicia geocodificação automática ao abrir
  useEffect(() => {
    if (!open) return;
    setPhase('geocodificando');
    setEnderecoManual(enderecoAtual ?? '');
    setErroMsg('');

    api
      .post(`/visitas/clientes/${clienteId}/geocodificar`)
      .then(() => onSuccess())
      .catch(() => setPhase('editando'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clienteId]);

  const handleBuscarManual = async () => {
    if (!enderecoManual.trim()) return;
    setPhase('buscando_manual');
    setErroMsg('');
    try {
      await api.post(`/visitas/clientes/${clienteId}/geocodificar`, {
        endereco_manual: enderecoManual.trim()
      });
      onSuccess();
    } catch (err) {
      const serverMsg = extractApiError(err);
      setPhase('nao_encontrado');
      setErroMsg(
        serverMsg.includes('localizar')
          ? serverMsg
          : `Não foi possível localizar "${enderecoManual.trim()}". Verifique o endereço e tente novamente.`
      );
    }
  };

  const isBusy = phase === 'geocodificando' || phase === 'buscando_manual';

  return (
    <Dialog open={open} onClose={isBusy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <MyLocationIcon color="primary" />
          <span>Localizar cliente</span>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>{clienteNome}</strong> não tem coordenadas GPS cadastradas.
        </Typography>

        {phase === 'geocodificando' && (
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Buscando endereço automaticamente...</Typography>
          </Box>
        )}

        {(phase === 'editando' || phase === 'nao_encontrado') && (
          <Box mt={2}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Não conseguimos localizar o endereço automaticamente. Informe o endereço completo para
              tentar novamente:
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Endereço completo"
              placeholder="Ex: Rua das Flores, 123, Centro, São Paulo, SP"
              value={enderecoManual}
              onChange={(e) => setEnderecoManual(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleBuscarManual();
              }}
              autoFocus
            />
            {erroMsg && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {erroMsg}
              </Alert>
            )}
          </Box>
        )}

        {phase === 'buscando_manual' && (
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Buscando "{enderecoManual}"...</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isBusy}>
          Cancelar
        </Button>
        {(phase === 'editando' || phase === 'nao_encontrado') && (
          <Button
            variant="contained"
            onClick={() => void handleBuscarManual()}
            disabled={!enderecoManual.trim()}
          >
            Buscar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
